// P2: Subset-sum match with exact unit price + vat_rate_code + currency.

import { absRound } from '../../../calc.ts';
import type { MatchCandidate, MatchLineItem, NearMissCandidate, UnmatchedReason, UsedCandidates } from './types.ts';
import type { InvoiceLink } from '@shared/types/invoice.ts';
import { verifyMultiMatch } from './verify.ts';
import { filterCandidates, markUsed } from './candidates.ts';
import { findSubset, findDisjointSubset, type SubsetDiagnostics } from './subsetSum.ts';
import { P2_LOOKBACK_MONTHS } from './scoreConfig.ts';

export interface P2Result {
  links: InvoiceLink[];
  /** Populated when no subset was found — top candidates that were considered. */
  nearMisses?: NearMissCandidate[];
  /** A specific unmatched reason when links is empty (REC-M6 / finding 6):
   *  - `p2_no_subset`       — candidates existed but no subset summed to the target;
   *  - `p2_verify_failed`   — a subset summed but verification (weight/vat/gross) failed;
   *  - `p2_search_exhausted`— the DFS hit its iteration cap before finding a subset;
   *  - `p2_ambiguous`       — a verified subset was found but a second disjoint subset also
   *                           sums to the target → downgraded to a report-only near-miss. */
  reason?: UnmatchedReason;
  /** Finding 12: a second, DISJOINT subset also sums to the target → the match is
   *  ambiguous and is DOWNGRADED to a report-only near-miss (no link written). */
  ambiguous?: boolean;
}

/** How many bounded retries to attempt when a found subset fails verification (REC-M6):
 *  each retry excludes the failed combination and re-searches, so a genuine match hiding
 *  behind a coincidental near-sum can still be found. */
const MAX_VERIFY_RETRIES = 3;

/**
 * Match by finding a subset of candidates whose net amounts sum to the line item's net.
 * Candidates must match exact unit price, vat_rate_code, and currency.
 * Returns result with links (empty when no match) and optional near-misses.
 * Returns null when no candidates at all were available.
 */
export function matchBySubsetSum(
  invoice_id: number,
  li: MatchLineItem,
  candidates: MatchCandidate[],
  currency: string,
  sell_date: string | null | undefined,
  used: UsedCandidates,
  allowAdjacentMonth = false,
): P2Result | null {
  const targetNet = absRound(li.net_amount ?? 0);
  const abs_unit_price = absRound(li.unit_price ?? 0);

  // Try with exact price filter first, then without (cross-price-type: flat_rate line vs
  // per_ton candidates). Both apply the A14 lower date bound (lookbackMonths).
  let filtered = filterCandidates(candidates, {
    currency, sell_date, used,
    vat_rate_code: li.vat_rate_code,
    price_filter: true,
    abs_unit_price,
    allowAdjacentMonth,
    lookbackMonths: P2_LOOKBACK_MONTHS,
  });
  const diag: SubsetDiagnostics = {};
  let indices = findSubset(filtered, targetNet, diag);

  if (!indices) {
    filtered = filterCandidates(candidates, {
      currency, sell_date, used,
      vat_rate_code: li.vat_rate_code,
      price_filter: false,
      abs_unit_price,
      allowAdjacentMonth,
      lookbackMonths: P2_LOOKBACK_MONTHS,
    });
    indices = findSubset(filtered, targetNet, diag);
  }

  if (!indices) {
    if (filtered.length === 0) return null;
    // DFS exhausted its budget → distinct from "no subset exists".
    if (diag.exhausted) {
      return { links: [], reason: 'p2_search_exhausted', nearMisses: buildNearMisses(filtered, targetNet, 'p2_search_exhausted') };
    }
    return { links: [], reason: 'p2_no_subset', nearMisses: buildNearMisses(filtered, targetNet, 'p2_no_subset') };
  }

  // A14: the DATE window for P2 is enforced at FILTER time — a WIDER band than P1's
  // same-month gate: [sell_date − P2_LOOKBACK_MONTHS, sell_date + (same/adjacent month)].
  // So verify is deliberately NOT given the sell date here (passing it would re-impose
  // P1's strict same-month gate and defeat the wider lookback). Verify still checks
  // weight / vat / gross / unit_price on the subset. The pre-fix bug was the MISSING
  // filter lower bound (letting arbitrarily-old orphans subset-sum in), now closed.
  const excluded: number[][] = [];
  for (let attempt = 0; attempt <= MAX_VERIFY_RETRIES && indices; attempt++) {
    const subset = indices.map(i => filtered[i]);
    const positions = subset.map(({ candidate: c }) => ({ ...c.pricing, vat_rate_code: c.vat_rate_code }));
    const weights = subset.map(({ candidate: c }) => c.weight);
    const verifyResult = verifyMultiMatch(li, positions, undefined, undefined, weights);

    if (verifyResult.verified) {
      // Finding 12: does a SECOND, disjoint subset also sum to the target? If so the match
      // is ambiguous → report it (with the runner-up) but write NO link (safe behavior).
      const runnerUp = findDisjointSubset(filtered, targetNet, indices);
      if (runnerUp) {
        const combined = [...subset, ...runnerUp.map(i => filtered[i])];
        return { links: [], reason: 'p2_ambiguous', ambiguous: true, nearMisses: buildNearMisses(combined, targetNet, 'p2_ambiguous') };
      }
      const links: InvoiceLink[] = [];
      for (const { candidate: c } of subset) {
        links.push({ ...c, invoice_id, invoice_line_item_id: li.id });
        markUsed(used, c);
      }
      return { links };
    }

    // REC-M6: verification failed. Retry excluding THIS combination so a genuine match
    // behind a coincidental near-sum can still be found (bounded).
    excluded.push(indices);
    indices = findSubsetExcluding(filtered, targetNet, excluded);
  }

  // Exhausted retries with only verify failures → distinct p2_verify_failed reason (not
  // reported as no_candidates, which wrongly told the operator "no transactions").
  return { links: [], reason: 'p2_verify_failed', nearMisses: buildNearMisses(filtered, targetNet, 'p2_verify_failed') };
}

/** Find a subset excluding any of the previously-tried combinations (order-independent). */
function findSubsetExcluding(
  filtered: { net: number }[],
  targetNet: number,
  excluded: number[][],
): number[] | null {
  const excludedKeys = new Set(excluded.map(c => [...c].sort((a, b) => a - b).join(',')));
  // Cheap approach: drop one member of each excluded combo in turn and re-search. For the
  // common single-candidate exclusion this is exact; for multi it's a bounded heuristic.
  for (const combo of excluded) {
    const dropSet = new Set(combo);
    const remaining: { net: number; origIdx: number }[] = [];
    filtered.forEach((c, i) => { if (!dropSet.has(i)) remaining.push({ net: c.net, origIdx: i }); });
    const found = findSubset(remaining, targetNet);
    if (found) {
      const mapped = found.map(i => remaining[i].origIdx);
      if (!excludedKeys.has([...mapped].sort((a, b) => a - b).join(','))) return mapped;
    }
  }
  return null;
}

function buildNearMisses(
  filtered: { candidate: MatchCandidate; net: number }[],
  targetNet: number,
  reasonPrefix: UnmatchedReason,
): NearMissCandidate[] {
  return filtered.slice(0, 3).map(({ candidate: c, net }) => ({
    type: c.entity_type,
    entity_id: c.entity_id,
    side: c.side ?? null,
    reference_number: c.reference_number ?? null,
    net_amount: net,
    date: c.date ?? null,
    weight: c.weight ?? null,
    disqualified_by: `${reasonPrefix}: target=${targetNet}, candidate_net=${net}, gap=${absRound(targetNet - net)}`,
  }));
}
